import { blobMediaUrl } from "@/lib/blob-url"
import { isAvailable, type Availability } from "@/lib/availability"
import type { PriceData } from "@/lib/pricing"

/* ───────────────────── MRI package catalogue ─────────────────────
 * The full-body-MRI package tiers (Core/Advanced/Pro). Pure catalogue data
 * with no client-only dependencies, so both the client `MriPackageCards`
 * component and the server test/pricing pages can read it — the latter build
 * their Product/Offer JSON-LD from it, which needs `image` for Google Merchant
 * listings. Lived in the "use client" mri-package-cards.tsx before; a server
 * component importing a value from a client module gets a client reference,
 * not the array, so the data has to sit in a server-safe module. */
export const MRI_PACKAGES = [
  {
    name: "Core",
    // Total visit time, arrival to departure — not scan time (AHD-13286).
    duration: "~60 min",
    descriptionKey: "pkgCoreDescription" as const,
    // Member rate, mirrors MEMBER_PRICING_ITEMS on the membership page (the
    // catalog source of truth). CHF-only.
    memberPrice: "1890",
    priceKey: "core",
    ctaKey: "bookCore" as const,
    includesLabelKey: "pkgCoreIncludesLabel" as const,
    bookingId: "3b7895a5-b892-44f9-9175-c2f7fd8e7962",
    features: [
      { nameKey: "featureFullBodyMri" as const, ai: false },
      { nameKey: "featureBrainMri" as const, ai: false },
      { nameKey: "featureSpineMri" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-core.png"),
    variant: "green" as const,
    highlighted: false,
    // Austria sells Advanced + Pro only (AHD-13988). Hidden there rather than
    // removed, so CH keeps its three tiers; filter with `mriPackagesFor`.
    availability: { "at-de": false } as Availability,
  },
  {
    name: "Advanced",
    duration: "~75 min",
    descriptionKey: "pkgAdvancedDescription" as const,
    memberPrice: "2290", // mirrors the membership catalog
    priceKey: "advanced",
    ctaKey: "bookAdvanced" as const,
    includesLabelKey: "pkgAdvancedIncludesLabel" as const,
    bookingId: "5147c324-1776-4e0a-9cc4-cff5bbcb18c0",
    features: [
      { nameKey: "featureAdvancedBloodTest" as const, ai: false },
      { nameKey: "featureBodyComposition" as const, ai: true },
      { nameKey: "featureProstateOvariesMri" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-advanced.png"),
    variant: "blue" as const,
    highlighted: false,
    mostPopular: true,
  },
  {
    name: "Pro",
    duration: "~90 min",
    descriptionKey: "pkgProDescription" as const,
    memberPrice: "3290", // mirrors the membership catalog
    priceKey: "pro",
    ctaKey: "bookPro" as const,
    includesLabelKey: "pkgProIncludesLabel" as const,
    bookingId: "64c6c5cb-95c4-4310-b8b5-ff68abfe7b38",
    features: [
      { nameKey: "featureBrainVolumetry" as const, ai: true },
      { nameKey: "featureHipsKneesMri" as const, ai: false },
      { nameKey: "featureHormonesVitamins" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-pro.png"),
    variant: "pink" as const,
    highlighted: false,
  },
]

export type MriPackage = (typeof MRI_PACKAGES)[number]

/** The MRI tiers a market sells, in catalogue order. Every surface that lists
 *  or prices the tiers reads this instead of `MRI_PACKAGES` directly, so a
 *  tier switched off for a market (Core in Austria) drops out everywhere. */
export function mriPackagesFor(country: string, locale: string): MriPackage[] {
  return MRI_PACKAGES.filter((pkg) =>
    isAvailable(
      "availability" in pkg ? pkg.availability : undefined,
      country,
      locale
    )
  )
}

/** The `priceKey`s (`core` / `advanced` / `pro`) of the tiers a market sells. */
export function mriPackageKeysFor(country: string, locale: string): string[] {
  return mriPackagesFor(country, locale).map((pkg) => pkg.priceKey)
}

/** Whether `id` names an MRI tier this market does not sell — `core` in
 *  Austria. Any other id (blood, add-ons) is never "unsold" here. */
export function isUnsoldMriTier(
  id: string,
  country: string,
  locale: string
): boolean {
  return (
    MRI_PACKAGES.some((pkg) => pkg.priceKey === id) &&
    !mriPackageKeysFor(country, locale).includes(id)
  )
}

/** `priceKey`s of the MRI tiers switched off for a country (any of its
 *  `${country}-<lang>` availability keys is false) — Core in Austria. The
 *  price feed drops these, so a stray Stripe price for a tier the market
 *  doesn't sell can't surface on any page. */
export function unsoldMriTierKeys(country: string): string[] {
  return MRI_PACKAGES.filter((pkg) => {
    const availability = "availability" in pkg ? pkg.availability : undefined
    return (
      availability !== undefined &&
      Object.entries(availability).some(
        ([key, sold]) => key.startsWith(`${country}-`) && sold === false
      )
    )
  }).map((pkg) => pkg.priceKey)
}

/** The cheapest tier a market sells — what a "from" price or a default
 *  package filter for the full-body MRI points at (Advanced in Austria, Core
 *  elsewhere). */
export function entryMriTierKey(country: string): string {
  const unsold = unsoldMriTierKeys(country)
  return MRI_PACKAGES.find((pkg) => !unsold.includes(pkg.priceKey))!.priceKey
}

/** The full-body MRI "from" price: the entry tier's price in this market.
 *  Empty when the feed has no figure for it. */
export function mriFromPrice(prices: PriceData, country: string): string {
  return prices.fullPrices[entryMriTierKey(country)] ?? ""
}
